import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  FileText,
  Users,
  LayoutDashboard,
  Workflow,
  Calendar,
  CheckSquare,
  UserCircle,
  Building,
  GitBranch,
  FileCode,
  X
} from 'lucide-react';

interface Command {
  id: string;
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  action: () => void;
  keywords: string[];
  category: 'navigation' | 'action' | 'search';
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  // Keyboard shortcut: Cmd+K or Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const commands: Command[] = [
    // Navigation
    {
      id: 'nav-dashboard',
      title: 'Dashboard',
      subtitle: 'Visão geral do sistema',
      icon: <LayoutDashboard className="w-5 h-5" />,
      action: () => navigate('/dashboard'),
      keywords: ['dashboard', 'home', 'inicio', 'painel'],
      category: 'navigation',
    },
    {
      id: 'nav-contracts',
      title: 'Contratos',
      subtitle: 'Ver todos os contratos',
      icon: <FileText className="w-5 h-5" />,
      action: () => navigate('/contracts'),
      keywords: ['contratos', 'contracts', 'documentos'],
      category: 'navigation',
    },
    {
      id: 'nav-templates',
      title: 'Templates',
      subtitle: 'Modelos de contratos',
      icon: <FileCode className="w-5 h-5" />,
      action: () => navigate('/templates'),
      keywords: ['templates', 'modelos', 'models'],
      category: 'navigation',
    },
    {
      id: 'nav-workflows',
      title: 'Workflows',
      subtitle: 'Fluxos de aprovação',
      icon: <Workflow className="w-5 h-5" />,
      action: () => navigate('/workflows'),
      keywords: ['workflows', 'fluxos', 'aprovacao', 'approval'],
      category: 'navigation',
    },
    {
      id: 'nav-calendar',
      title: 'Calendário',
      subtitle: 'Vencimentos e obrigações',
      icon: <Calendar className="w-5 h-5" />,
      action: () => navigate('/calendar'),
      keywords: ['calendario', 'calendar', 'vencimentos', 'prazos'],
      category: 'navigation',
    },
    {
      id: 'nav-teams',
      title: 'Equipes',
      subtitle: 'Gerenciar equipes',
      icon: <Users className="w-5 h-5" />,
      action: () => navigate('/teams'),
      keywords: ['equipes', 'teams', 'grupos'],
      category: 'navigation',
    },
    {
      id: 'nav-approvals',
      title: 'Minhas Aprovações',
      subtitle: 'Pendências para aprovar',
      icon: <CheckSquare className="w-5 h-5" />,
      action: () => navigate('/my-approvals'),
      keywords: ['aprovacoes', 'approvals', 'pendencias'],
      category: 'navigation',
    },
    {
      id: 'nav-profile',
      title: 'Meu Perfil',
      subtitle: 'Configurações do usuário',
      icon: <UserCircle className="w-5 h-5" />,
      action: () => navigate('/profile'),
      keywords: ['perfil', 'profile', 'usuario', 'conta'],
      category: 'navigation',
    },
    {
      id: 'nav-org-settings',
      title: 'Configurações da Organização',
      subtitle: 'Gerenciar organização',
      icon: <Building className="w-5 h-5" />,
      action: () => navigate('/settings'),
      keywords: ['organizacao', 'organization', 'configuracoes', 'settings'],
      category: 'navigation',
    },

    // Actions
    {
      id: 'action-new-contract',
      title: 'Novo Contrato',
      subtitle: 'Criar um novo contrato',
      icon: <FileText className="w-5 h-5 text-accent" />,
      action: () => navigate('/contracts/new'),
      keywords: ['novo', 'criar', 'new', 'create', 'contrato'],
      category: 'action',
    },
    {
      id: 'action-new-workflow',
      title: 'Novo Workflow',
      subtitle: 'Criar novo fluxo de aprovação',
      icon: <GitBranch className="w-5 h-5 text-chart-4" />,
      action: () => navigate('/workflows/new'),
      keywords: ['novo', 'criar', 'workflow', 'fluxo'],
      category: 'action',
    },
  ];

  const filteredCommands = search
    ? commands.filter((cmd) => {
        const searchLower = search.toLowerCase();
        return (
          cmd.title.toLowerCase().includes(searchLower) ||
          cmd.subtitle?.toLowerCase().includes(searchLower) ||
          cmd.keywords.some((kw) => kw.includes(searchLower))
        );
      })
    : commands;

  const handleSelect = useCallback((command: Command) => {
    command.action();
    setOpen(false);
    setSearch('');
  }, []);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />

      {/* Command Palette */}
      <div className="relative min-h-screen flex items-start justify-center p-4 sm:p-8">
        <div className="relative w-full max-w-2xl bg-background rounded-xl shadow-2xl overflow-hidden mt-[10vh] animate-fade-in">
          {/* Search Input */}
          <div className="flex items-center border-b border-border px-4">
            <Search className="w-5 h-5 text-muted-foreground flex-shrink-0" />
            <input
              type="text"
              placeholder="Digite para buscar... (Cmd+K)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 px-4 py-4 bg-transparent border-none outline-none text-foreground placeholder-muted-foreground"
              autoFocus
            />
            <button
              onClick={() => setOpen(false)}
              className="p-2 hover:bg-muted rounded-sm transition-colors"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>

          {/* Results */}
          <div className="max-h-[60vh] overflow-y-auto">
            {filteredCommands.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <Search className="w-12 h-12 mx-auto mb-4 opacity-40" />
                <p>Nenhum resultado encontrado</p>
              </div>
            ) : (
              <div className="p-2">
                {/* Navigation Commands */}
                {filteredCommands.some((c) => c.category === 'navigation') && (
                  <div className="mb-4">
                    <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Navegação
                    </div>
                    {filteredCommands
                      .filter((c) => c.category === 'navigation')
                      .map((command) => (
                        <CommandItem
                          key={command.id}
                          command={command}
                          onSelect={handleSelect}
                        />
                      ))}
                  </div>
                )}

                {/* Action Commands */}
                {filteredCommands.some((c) => c.category === 'action') && (
                  <div>
                    <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Ações Rápidas
                    </div>
                    {filteredCommands
                      .filter((c) => c.category === 'action')
                      .map((command) => (
                        <CommandItem
                          key={command.id}
                          command={command}
                          onSelect={handleSelect}
                        />
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-border px-4 py-3 bg-muted/50">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-1 bg-background border border-border rounded">↑↓</kbd>
                  Navegar
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-1 bg-background border border-border rounded">Enter</kbd>
                  Selecionar
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-1 bg-background border border-border rounded">Esc</kbd>
                  Fechar
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CommandItem({
  command,
  onSelect,
}: {
  command: Command;
  onSelect: (command: Command) => void;
}) {
  return (
    <button
      onClick={() => onSelect(command)}
      className="w-full flex items-center gap-3 px-3 py-3 rounded-sm hover:bg-muted transition-colors text-left group"
    >
      <div className="flex-shrink-0 text-muted-foreground group-hover:text-foreground">
        {command.icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-foreground truncate">
          {command.title}
        </div>
        {command.subtitle && (
          <div className="text-sm text-muted-foreground truncate">
            {command.subtitle}
          </div>
        )}
      </div>
    </button>
  );
}
